/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description GuarantorTableModal
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import _ from 'lodash';
import { Button, Card, Modal, Pagination, Row } from 'antd';
import Table, { renderPaginationTotal } from 'lib/antd/Table';

import type { ColumnsType } from 'antd/es/table';
import PaymentListTooltip from 'components/payments/common/PaymentListTooltip';
import { useGuarantorStoreActions, useGuarantorStoreValues } from 'store/guarantor';
import useSearchGuarantors from 'hooks/patients/useSearchGuarantors';
import { getName } from 'utils/parseFhirDataType';
import { formatAmount, getAccountNumber } from 'utils/accounts/allAccounts';
import RenderAccountList from 'components/payments/allPayments/RenderAccountList';
import GuarantorSearch from './GuarantorSearch';

type IGuarantorTableData = {
	key: string;
	id: string;
	name: string;
	reference: string;
	accounts: { id: string; accountAge: string | number; accountNumber?: string }[];
	patients: string[];
	pendingPR: number;
};
const GuarantorTableModal: React.FC<{
	value: PatientPayment.IGuarantor;
	onSelect: (selectedRow: PatientPayment.IGuarantor) => void;
	onCancel: () => void;
}> = ({ value: selectedGuarantor, onSelect, onCancel }) => {
	const [selectedRow, setSelectedRow] = useState<PatientPayment.IGuarantor | undefined>(selectedGuarantor);
	const [selectedRowKey, setSelectedRowKey] = useState<string | undefined>(selectedGuarantor?.key);

	const { apiParams, pageNumber, pageSize } = useGuarantorStoreValues();
	const { setPageNumber, setPageSize } = useGuarantorStoreActions();

	const { data, isFetching, isError } = useSearchGuarantors({
		guarantorSearch: _.cloneDeep(apiParams),
		pgNum: pageNumber,
		pgSize: pageSize,
	});

	const rows = useMemo(() => {
		const list = data?.data || [];
		return _.map(list, (item) => {
			const name = getName(item.fhirResource);
			const accounts = _.map(item.accounts, ({ accountAge, accountId, identifier }) => ({
				id: accountId,
				accountAge,
				accountNumber: getAccountNumber(identifier),
			}));
			const patients = _.map(item.fhirPatients, (patient) => getName(patient));
			const pendingPR = item?.metadata?.pendingPR || 0;
			const reference = `${item.fhirResource?.resourceType}/${item.fhirResource?.id}`;
			return {
				key: item.id,
				id: item.id,
				name,
				reference,
				accounts,
				patients,
				pendingPR,
			};
		});
	}, [data]);

	const handleSelectGuarantor = useCallback((row: IGuarantorTableData) => {
		if (!row) {
			return;
		}
		setSelectedRowKey(row.key);
		setSelectedRow({ key: row.id, id: row.id, name: row.name, reference: row.reference || '' });
	}, []);

	useEffect(() => {
		if (!selectedGuarantor?.key) {
			setSelectedRow(undefined);
			setSelectedRowKey(undefined);
			return;
		}
		const matchingRow = rows.find((row) => row.key === selectedGuarantor.key);
		if (matchingRow) {
			handleSelectGuarantor(matchingRow);
			return;
		}
		setSelectedRow(selectedGuarantor);
		setSelectedRowKey(selectedGuarantor.key);
	}, [handleSelectGuarantor, rows, selectedGuarantor]);

	const renderPatients = (patients: string[]) => {
		const firstPatient = _.first(patients);
		if (!firstPatient) {
			return '-';
		}
		if (patients.length === 1) {
			return <span>{firstPatient}</span>;
		}

		return (
			<PaymentListTooltip title='Patients' items={patients}>
				<span className='inline-flex items-center gap-0'>
					<span>{firstPatient}</span>
					<Button type='link' className='p-0'>{` +${patients.length - 1}`}</Button>
				</span>
			</PaymentListTooltip>
		);
	};

	const columns: ColumnsType<IGuarantorTableData> = useMemo(
		() => [
			{
				title: 'Guarantor',
				dataIndex: 'name',
				key: 'name',
				width: 200,
			},
			{
				title: 'No. of Accounts',
				dataIndex: 'accounts',
				key: 'accounts',
				render: (value: IGuarantorTableData['accounts']) =>
					_.isArray(value) && value.length > 0 ? <RenderAccountList accounts={value} /> : '-',
			},
			{
				title: 'Patient Name(s)',
				dataIndex: 'patients',
				key: 'patients',
				render: (value: IGuarantorTableData['patients']) => (_.isArray(value) && value.length > 0 ? renderPatients(value) : '-'),
			},
			{
				title: 'Pending PR',
				dataIndex: 'pendingPR',
				align: 'right',
				key: 'pendingPR',
				render: (value?: number) => (value ? formatAmount(value) : '-'),
			},
		],
		[],
	);

	useEffect(() => {
		if (selectedGuarantor?.key || rows.length === 0) {
			return;
		}
		const isSelectedRowInData = selectedRowKey && rows.some((row) => row.key === selectedRowKey);
		if (!isSelectedRowInData) {
			handleSelectGuarantor(rows[0]);
		}
	}, [handleSelectGuarantor, rows, selectedGuarantor, selectedRowKey]);

	const rowSelection = useMemo(
		() => ({
			type: 'radio' as const,
			selectedRowKeys: selectedRowKey ? [selectedRowKey] : [_.first(rows)?.key],
			onChange: (__: AnyType, selectedRows: IGuarantorTableData[]) => {
				const row = selectedRows?.[0];
				if (row) {
					handleSelectGuarantor(row);
				}
			},
		}),
		[handleSelectGuarantor, rows, selectedRowKey],
	);

	return (
		<Modal
			open={true}
			title='Select Guarantor'
			onCancel={onCancel}
			okText='Confirm'
			onOk={() => {
				if (selectedRow) {
					onSelect(selectedRow);
				}
			}}
			okButtonProps={{
				disabled: !selectedRow?.id,
			}}
			width={800}
			maskClosable={false}
		>
			<Card
				title={
					<div style={{ padding: '3px 0' }}>
						<GuarantorSearch />
					</div>
				}
				classNames={{ body: 'border-[unset] p-0', actions: 'card-pagination-action' }}
				actions={
					data?.count > 0 && [
						<Row key='pagination' className='mx-[16px]' justify='end'>
							<Pagination
								responsive
								showSizeChanger
								current={pageNumber}
								pageSize={pageSize}
								showTotal={renderPaginationTotal}
								total={data?.count}
								onChange={(newPageNumber) => setPageNumber(newPageNumber)}
								onShowSizeChange={(newPageNumber, newPageSize) => {
									setPageNumber(newPageNumber);
									setPageSize(newPageSize);
								}}
							/>
						</Row>,
					]
				}
			>
				<Table
					rowKey='key'
					bordered
					className='guarantor-accounts-table'
					size='small'
					columns={columns}
					dataSource={rows}
					pagination={false}
					loading={isFetching}
					error={isError}
					scroll={{ y: '50vh' }}
					rowSelection={rowSelection}
					onRow={(record) => ({
						onClick: () => handleSelectGuarantor(record),
					})}
				/>
			</Card>
		</Modal>
	);
};

export default GuarantorTableModal;
